import { styled } from '@mui/material/styles';
import { Box, Container, Typography, Skeleton } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import { staggerContainer, fadeUp, reducedMotionVariants } from '../../../theme/animations';
import { SectionTitle, SectionSubtitle } from './SectionHeader';
import { StarRating } from '../../venues/components/StarRating';
import { useFeaturedReviews } from '../hooks/useFeaturedReviews';
import type { FeaturedReview } from '@lov/shared';

// ─── Styled Components ────────────────────────────────────────────────────────

const SectionContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(12, 0),
  background: theme.palette.background.default,
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(8, 0),
  },
}));

const CarouselTrack = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(3),
  overflowX: 'auto',
  scrollSnapType: 'x mandatory',
  WebkitOverflowScrolling: 'touch',
  paddingBottom: theme.spacing(2),
  // Hide scrollbar visually but keep it functional
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.divider} transparent`,
  '&::-webkit-scrollbar': {
    height: 4,
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.divider,
    borderRadius: 2,
  },
}));

const ReviewCard = styled(Box)(({ theme }) => ({
  flex: '0 0 calc(33.333% - 16px)',
  scrollSnapAlign: 'start',
  borderRadius: 20,
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
  [theme.breakpoints.down('md')]: {
    flex: '0 0 calc(100% - 0px)',
  },
}));

const SkeletonCard = styled(Box)(({ theme }) => ({
  flex: '0 0 calc(33.333% - 16px)',
  scrollSnapAlign: 'start',
  borderRadius: 20,
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    flex: '0 0 calc(100% - 0px)',
  },
}));

const ReviewComment = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: '0.9375rem',
  color: theme.palette.text.secondary,
  lineHeight: 1.7,
  flexGrow: 1,
  display: '-webkit-box',
  WebkitLineClamp: 5,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
}));

const CustomerName = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamilyMono,
  fontWeight: 700,
  fontSize: '1rem',
  color: theme.palette.text.primary,
}));

const VenueName = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: '0.8125rem',
  color: theme.palette.text.secondary,
}));

const ReviewDate = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: '0.75rem',
  color: theme.palette.text.disabled,
}));

const EmptyStateText = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: '1rem',
  color: theme.palette.text.secondary,
  textAlign: 'center',
  padding: theme.spacing(6, 0),
}));

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(dateString: string): string {
  try {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

function truncateComment(comment: string, maxLength = 150): string {
  if (comment.length <= maxLength) return comment;
  return comment.slice(0, maxLength).trimEnd() + '\u2026';
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function TestimonialSkeletons() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <SkeletonCard key={i}>
          <Skeleton variant="rounded" width={100} height={20} />
          <Skeleton variant="text" sx={{ fontSize: '0.9375rem' }} />
          <Skeleton variant="text" sx={{ fontSize: '0.9375rem' }} />
          <Skeleton variant="text" sx={{ fontSize: '0.9375rem' }} width="80%" />
          <Box sx={{ mt: 'auto' }}>
            <Skeleton variant="text" width={120} sx={{ fontSize: '1rem' }} />
            <Skeleton variant="text" width={90} sx={{ fontSize: '0.8125rem' }} />
          </Box>
        </SkeletonCard>
      ))}
    </>
  );
}

function TestimonialCard({
  review,
  variants,
}: {
  review: FeaturedReview;
  variants: typeof fadeUp;
}) {
  return (
    <motion.div variants={variants} style={{ display: 'contents' }}>
      <ReviewCard>
        <StarRating rating={review.rating} size={18} />
        <ReviewComment>{truncateComment(review.comment)}</ReviewComment>
        <Box>
          <CustomerName>{review.customerName}</CustomerName>
          <VenueName>{review.venueName}</VenueName>
          <ReviewDate>{formatDate(review.date)}</ReviewDate>
        </Box>
      </ReviewCard>
    </motion.div>
  );
}

// ─── Inner Section ────────────────────────────────────────────────────────────

function TestimonialsSectionInner() {
  const { reviews, isLoading } = useFeaturedReviews();
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = shouldReduceMotion ? reducedMotionVariants : staggerContainer;
  const itemVariants = shouldReduceMotion ? reducedMotionVariants : fadeUp;

  return (
    <SectionContainer id="testimonials">
      <Container maxWidth="lg">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <motion.div variants={itemVariants}>
            <SectionTitle>What Players Are Saying</SectionTitle>
            <SectionSubtitle>
              Real experiences from real players who book through Lord of Venues.
            </SectionSubtitle>
          </motion.div>

          <CarouselTrack role="list" aria-label="Customer testimonials">
            {isLoading ? (
              <TestimonialSkeletons />
            ) : reviews.length === 0 ? (
              <Box sx={{ width: '100%' }}>
                <EmptyStateText>Be the first to leave a review!</EmptyStateText>
              </Box>
            ) : (
              reviews.map((review) => (
                <TestimonialCard key={review.id} review={review} variants={itemVariants} />
              ))
            )}
          </CarouselTrack>
        </motion.div>
      </Container>
    </SectionContainer>
  );
}

// ─── Public Export (Feature-Gated) ───────────────────────────────────────────

export function TestimonialsSection() {
  return <TestimonialsSectionInner />;
}
